import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import CalendarGrid, { ymd, parseYmd } from "@/components/CalendarGrid";
import AssignTaskModal from "@/components/AssignTaskModal";
import AssignmentActions from "@/components/common/AssignmentActions";
import ScopeToggle, { readScope } from "@/components/common/ScopeToggle";
import { Plus } from "lucide-react";

export default function ManagerCalendarPage() {
  const [monthDate, setMonthDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [selectedYmd, setSelectedYmd] = useState(ymd(new Date()));
  const [showAssign, setShowAssign] = useState(false);
  const [scope, setScope] = useState(readScope("mm.tasks.scope", "mine"));

  const load = async () => {
    try {
      const [t, c, e] = await Promise.all([
        api.get(`/tasks?scope=${scope}`),
        api.get("/campaigns"),
        api.get("/marketing/my-executives"),
      ]);
      setTasks(t.data);
      setCampaigns(c.data);
      setExecutives(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [scope]);

  const userById = Object.fromEntries(executives.map((u) => [u.id, u]));

  const tasksByDay = tasks.reduce((acc, t) => {
    const d = (t.scheduled_at || "").slice(0, 10);
    if (!d) return acc;
    (acc[d] = acc[d] || []).push(t);
    return acc;
  }, {});

  const dayTasks = tasksByDay[selectedYmd] || [];

  const legend = (
    <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-gray-500 mr-2">
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#d4af37]"/>Today</span>
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#d4af37]/70"/>Campaign</span>
    </div>
  );

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Marketing · Manager</p>
          <h1 className="font-display text-3xl text-white mt-1">Master Calendar</h1>
        </div>
        <div className="flex items-center gap-3">
          <ScopeToggle
            value={scope}
            onChange={setScope}
            storageKey="mm.tasks.scope"
            testIdPrefix="mm-tasks-scope"
          />
          <button className="iz-btn-primary flex items-center gap-2" onClick={() => setShowAssign(true)} data-testid="assign-objective-button">
            <Plus className="w-4 h-4" strokeWidth={2}/> Assign Objective
          </button>
        </div>
      </div>

      <div className="px-10 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <CalendarGrid
              monthDate={monthDate}
              onPrev={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
              onNext={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
              onSelectDay={setSelectedYmd}
              selectedYmd={selectedYmd}
              tasksByDay={tasksByDay}
              campaignsSpanning={campaigns}
              legend={legend}
            />
          </div>

          <div className="iz-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Selected day</p>
                <p className="font-display text-xl text-white">
                  {selectedYmd ? parseYmd(selectedYmd).toDateString() : "—"}
                </p>
              </div>
              <button
                onClick={() => setShowAssign(true)}
                className="iz-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1"
                data-testid="day-assign-button"
              >
                <Plus className="w-3 h-3"/> New
              </button>
            </div>
            <p className="text-xs text-gray-500 font-mono uppercase tracking-widest mb-3">Objectives · {dayTasks.length}</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {dayTasks.length === 0 && <p className="text-xs text-gray-600">No objectives for this day.</p>}
              {dayTasks.map(t => (
                <div key={t.id} className="p-3 rounded-md border border-[#262626] hover:border-[#d4af37]/50 transition-colors" data-testid={`day-task-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-white">{t.title}</p>
                    <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      t.status === "completed" ? "text-[#10b981] bg-[#10b981]/10"
                      : t.status === "in_progress" ? "text-[#d4af37] bg-[#d4af37]/10"
                      : "text-gray-400 bg-[#1a1a1a]"
                    }`}>{t.status.replace("_"," ")}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 font-mono">{(t.scheduled_at || "").slice(11, 16)} {t.location ? `· ${t.location}` : ""}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 iz-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Live feed</p>
              <p className="font-display text-xl text-white">
                {scope === "mine" ? "My objectives" : "Recent objectives across the team"}
              </p>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Latest 8</span>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#262626]">
                {["Title","Assignee","When","Status","Actions"].map(h => (
                  <th key={h} className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 py-2 pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasks.slice(-8).reverse().map(t => {
                const assignee = userById[t.assigned_to_user_id];
                return (
                  <tr key={t.id} className="border-b border-[#1a1a1a]" data-testid={`task-row-${t.id}`}>
                    <td className="py-2 pr-4 text-sm text-white">{t.title}</td>
                    <td className="py-2 pr-4 text-xs text-gray-400">
                      {assignee ? (
                        <span>{assignee.name}{assignee.role_name ? ` · ${assignee.role_name}` : ""}</span>
                      ) : (
                        <span className="font-mono">{(t.assigned_to_user_id || "").slice(0,8)}…</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs font-mono text-gray-500">{(t.scheduled_at || "").replace("T"," ").slice(0,16)}</td>
                    <td className="py-2 pr-4">
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${
                        t.status === "completed" ? "text-[#10b981]"
                        : t.status === "in_progress" ? "text-[#d4af37]"
                        : "text-gray-400"
                      }`}>{t.status.replace("_"," ")}</span>
                    </td>
                    <td className="py-2 pr-4">
                      <AssignmentActions
                        itemLabel={`${t.title}`}
                        selfAssignEndpoint={`/tasks/${t.id}/self-assign`}
                        reassignEndpoint={`/tasks/${t.id}/reassign`}
                        candidates={executives}
                        onDone={load}
                        testIdPrefix={`task-${t.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AssignTaskModal
        open={showAssign}
        defaultDate={selectedYmd}
        onClose={() => setShowAssign(false)}
        onSaved={() => { setShowAssign(false); load(); }}
      />
    </div>
  );
}
